import { LookDto } from "../look";
import { ProductDto } from "../product";
import { QueryClient } from "@tanstack/react-query";
import { createMemoCache } from "./utils";

/**
 * Normalized version of LookDto
 *
 * Replaces the products array with productIds array to:
 * 1. Prevent duplication of product data across multiple looks
 * 2. Allow independent product cache updates
 * 3. Support efficient data fetching patterns
 */
export type NormalizedLook = Omit<LookDto, "products"> & {
  productIds: string[];
};

/**
 * Extended error type for denormalization errors
 */
export class DenormalizationError extends Error {
  constructor(message: string, public readonly missingIds: string[] = []) {
    super(message);
    this.name = "DenormalizationError";
  }
}

// LRU cache for memoization of denormalized looks
const memoCache = createMemoCache<string, LookDto>(100, 60 * 1000);

/**
 * Normalizes a look by extracting products and storing them in the product cache
 * Returns a modified look object where the products array is replaced with productIds
 *
 * @param queryClient - QueryClient instance
 * @param look - Original look DTO from the API
 * @returns NormalizedLook - Look with products replaced by productIds
 */
export function normalizeLook(queryClient: QueryClient, look: LookDto): NormalizedLook {
  // Get products from the look
  const products = look.products || [];

  // Store each product in the product cache
  products.forEach((product: ProductDto) => {
    queryClient.setQueryData(["product", product.id], product);
  });

  // Create a normalized version of the look
  const { products: _, ...restLook } = look;
  const normalizedLook: NormalizedLook = {
    ...restLook,
    productIds: products.map((product) => product.id),
  };

  return normalizedLook;
}

/**
 * Batch normalizes multiple looks at once
 * More efficient than calling normalizeLook multiple times for large datasets
 *
 * @param queryClient - QueryClient instance
 * @param looks - Array of looks to normalize
 * @returns Array of normalized looks
 */
export function batchNormalizeLooks(queryClient: QueryClient, looks: LookDto[]): NormalizedLook[] {
  // Create a map to deduplicate products across all looks
  const productMap = new Map<string, ProductDto>();

  // Extract all products from all looks
  looks.forEach((look) => {
    (look.products || []).forEach((product) => {
      productMap.set(product.id, product);
    });
  });

  // Batch update the product cache
  productMap.forEach((product) => {
    queryClient.setQueryData(["product", product.id], product);
  });

  // Normalize each look
  return looks.map((look) => {
    const { products: _, ...restLook } = look;
    return {
      ...restLook,
      productIds: (look.products || []).map((p) => p.id),
    };
  });
}

/**
 * Denormalizes a look by fetching products from the product cache and
 * reconstructing the original LookDto structure with proper error handling
 *
 * @param queryClient - QueryClient instance
 * @param normalizedLook - Normalized look from the cache
 * @param options - Configuration options
 * @returns LookDto - Look with full product objects
 * @throws DenormalizationError if products are missing and throwOnMissing is true
 */
export function denormalizeLook(
  queryClient: QueryClient,
  normalizedLook: NormalizedLook,
  options: {
    throwOnMissing?: boolean;
    includePartial?: boolean;
  } = {}
): LookDto {
  // Default options
  const { throwOnMissing = false, includePartial = true } = options;

  // Generate cache key for memoization
  const cacheKey = `${normalizedLook.id}:${normalizedLook.productIds.join(",")}`;

  // Check if we have a cached result that's still valid
  const cachedResult = memoCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // Get product IDs from the normalized look
  const productIds = normalizedLook.productIds || [];
  const missingIds: string[] = [];

  // Get products from the product cache
  const products = productIds
    .map((id) => {
      const product = queryClient.getQueryData<ProductDto>(["product", id]);
      if (!product) missingIds.push(id);
      return product;
    })
    .filter(Boolean) as ProductDto[];

  // Handle missing products based on options
  if (missingIds.length > 0 && throwOnMissing) {
    throw new DenormalizationError(`Failed to denormalize look: ${missingIds.length} products missing`, missingIds);
  }

  // If no partial results wanted and we have missing products, return null
  if (missingIds.length > 0 && !includePartial && !throwOnMissing) {
    return null as unknown as LookDto;
  }

  // Reconstruct the original look structure
  const { productIds: _, ...restLook } = normalizedLook;
  const denormalizedLook: LookDto = {
    ...restLook,
    products: products,
  };

  // Store in memoization cache
  memoCache.set(cacheKey, denormalizedLook);

  return denormalizedLook;
}

/**
 * Normalizes an array of looks
 * Alias for batchNormalizeLooks for API consistency
 *
 * @param queryClient React Query client instance
 * @param looks Array of look DTOs
 * @returns Array of normalized look objects
 */
export function normalizeLooks(queryClient: QueryClient, looks: LookDto[]): NormalizedLook[] {
  return batchNormalizeLooks(queryClient, looks);
}

/**
 * Type guard to check if an object is a normalized look
 */
export function isNormalizedLook(obj: any): obj is NormalizedLook {
  return obj && typeof obj === "object" && "id" in obj && "productIds" in obj && Array.isArray(obj.productIds);
}

/**
 * Type guard to check if an object is a denormalized look
 */
export function isDenormalizedLook(obj: any): obj is LookDto {
  return obj && typeof obj === "object" && "id" in obj && "products" in obj && Array.isArray(obj.products);
}
